import { memo, useEffect } from 'react';
import { clsx } from 'clsx';
import useSearchableSelectOptions from '../../../hooks/useSearchableSelectOptions';
import useOutsideClick from '../../../hooks/dom/useOutsideClick';
import type { SelectBaseProps } from './Select';
import { useSelectInputState } from './Select';
import styles from './Select.module.css';
import SelectMenu from './SelectMenu';
import SelectDropdown from './SelectDropdown';
import VirtualizedSelectDropdown from './VirtualizedSelectDropdown';

export interface MultiSelectProps extends SelectBaseProps {
    value: string[];
    noMaxWidth?: boolean;
    onChange(value: string[]): void;
}

const MultiSelect = ({
    disabled = false,
    inline = false,
    fullWidth = false,
    noPadding = false,
    rightAligned = false,
    bordered = false,
    material = true,
    searchable = false,
    small = false,
    noMaxWidth = false,
    label,
    placeholder,
    value,
    options = [],
    onChange,
    menuSize,
    keepLabel = false,
    noBackground = false,
    boundsRef,
    rootNode,
    ...ariaProps
}: MultiSelectProps) => {
    const [containerRef, isOpen, setIsOpen] = useSelectInputState();
    const virtualized = options.length > 200;

    const { search, setSearch, filteredOptions } = useSearchableSelectOptions(
        options,
        searchable
    );

    const closeDropdown = () => {
        setIsOpen(false);
        setSearch('');
    };

    const onValueSelect = (newValue: string) => {
        const currentValues = new Set(value);

        if (currentValues.has(newValue)) {
            currentValues.delete(newValue);
        } else {
            currentValues.add(newValue);
        }

        onChange(Array.from(currentValues));
    };

    useOutsideClick({
        elementRef: containerRef,
        callback: () => {
            setIsOpen(false);
            setSearch('');
        },
        rootNode: rootNode,
    });

    useEffect(() => {
        if (!isOpen) setSearch('');
    }, [isOpen, setSearch]);

    return (
        <div
            className={clsx(styles.select, {
                [styles.isOpen]: isOpen,
                [styles.inline]: inline,
                [styles.fullWidth]: fullWidth,
                [styles.noMaxWidth]: noMaxWidth,
            })}
            ref={containerRef}
            {...ariaProps}
        >
            <SelectMenu
                small={small}
                disabled={disabled}
                bordered={bordered}
                material={material}
                label={label}
                placeholder={placeholder}
                selectedOption={options.filter(option =>
                    value.includes(option.value)
                )}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                search={search}
                updateSearch={setSearch}
                searchable={searchable}
                fullWidth={fullWidth}
                noPadding={noPadding}
                keepLabel={keepLabel}
                onChange={onValueSelect}
                noBackground={noBackground}
            />
            {isOpen &&
                (virtualized ? (
                    <VirtualizedSelectDropdown
                        menuSize={menuSize}
                        rightAligned={rightAligned}
                        value={value}
                        onChange={onValueSelect}
                        options={filteredOptions}
                        closeDropdown={closeDropdown}
                        boundsRef={boundsRef}
                    />
                ) : (
                    <SelectDropdown
                        menuSize={menuSize}
                        rightAligned={rightAligned}
                        value={value}
                        onChange={onValueSelect}
                        options={filteredOptions}
                        closeDropdown={closeDropdown}
                        boundsRef={boundsRef}
                    />
                ))}
        </div>
    );
};

export default memo(MultiSelect);
